import { getCurrencySymbol } from '@angular/common';
import { Injectable, Type, inject } from '@angular/core';
import { NavigationCancel, NavigationEnd, NavigationStart, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Store, select } from '@ngrx/store';
import { Observable, combineLatest, from, merge, noop, of } from 'rxjs';
import {
  catchError,
  filter,
  map,
  sample,
  shareReplay,
  startWith,
  switchMap,
  take,
  withLatestFrom,
} from 'rxjs/operators';
import { LazyQuickViewComponent } from 'src/app/extensions/quick-view/exports/lazy-quick-view/lazy-quick-view.component';

import { FeatureToggleService } from 'ish-core/feature-toggle.module';
import {
  getAvailableLocales,
  getCurrentCurrency,
  getCurrentLocale,
  getDeviceType,
  getICMBaseURL,
  getPipelineEndpoint,
  getRestEndpoint,
} from 'ish-core/store/core/configuration';
import { businessError, getGeneralError, getGeneralErrorType } from 'ish-core/store/core/error';
import { selectPath } from 'ish-core/store/core/router';
import { getExtraConfigParameter, getServerConfigParameter } from 'ish-core/store/core/server-config';
import {
  getBreadcrumbData,
  getHeaderHeight,
  getHeaderType,
  getIsTransparentHeader,
  getSearchBoxActive,
  getWrapperClass,
  isStickyHeader,
  setSearchBoxActive,
} from 'ish-core/store/core/viewconf';
import { getLoggedInCustomer } from 'ish-core/store/customer/user';
import { getAllCountries, loadCountries } from 'ish-core/store/general/countries';
import { getRegionsByCountryCode, loadRegions } from 'ish-core/store/general/regions';
import { loadProduct } from 'ish-core/store/shopping/products';
import { whenTruthy } from 'ish-core/utils/operators';

@Injectable({ providedIn: 'root' })
export class AppFacade {
  private featureToggleService = inject(FeatureToggleService);

  constructor(private store: Store, private router: Router, private modalService: NgbModal) {
    this.routingInProgress$.subscribe(noop);

    store.pipe(select(getICMBaseURL)).subscribe(icmBaseUrl => (this.icmBaseUrl = icmBaseUrl));
  }
  icmBaseUrl: string;

  headerType$ = this.store.pipe(select(getHeaderType));
  deviceType$ = this.store.pipe(select(getDeviceType));
  stickyHeader$ = this.store.pipe(select(isStickyHeader));

  currentLocale$ = this.store.pipe(select(getCurrentLocale));
  availableLocales$ = this.store.pipe(select(getAvailableLocales));
  currentCurrency$ = this.store.pipe(select(getCurrentCurrency));

  generalError$ = this.store.pipe(select(getGeneralError));
  generalErrorType$ = this.store.pipe(select(getGeneralErrorType));
  breadcrumbData$ = this.store.pipe(select(getBreadcrumbData));

  getRestEndpoint$ = this.store.pipe(select(getRestEndpoint));
  getPipelineEndpoint$ = this.store.pipe(select(getPipelineEndpoint));

  getRestEndpointWithContext$ = combineLatest([
    this.store.pipe(select(getRestEndpoint)),
    this.store.pipe(
      select(getCurrentLocale),
      whenTruthy(),
      map(l => `;loc=${l}`)
    ),
    this.store.pipe(
      select(getCurrentCurrency),
      whenTruthy(),
      map(l => `;cur=${l}`)
    ),
  ]).pipe(map(arr => arr.join('')));

  appWrapperClasses$ = combineLatest([
    this.store.pipe(
      select(getWrapperClass),
      sample(this.router.events.pipe(filter(event => event instanceof NavigationEnd)))
    ),
    this.store.pipe(
      select(isStickyHeader),
      map(isSticky => (isSticky ? 'sticky-header' : ''))
    ),
    this.store.pipe(
      select(getDeviceType),
      map(deviceType => (deviceType === 'mobile' ? 'sticky-header' : ''))
    ),
  ]).pipe(map(classes => classes.filter(c => !!c)));

  routingInProgress$ = merge(
    this.router.events.pipe(
      filter(event => event instanceof NavigationStart),
      map(() => true)
    ),
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(() => false)
    ),
    this.router.events.pipe(
      filter(event => event instanceof NavigationCancel),
      map(() => false)
    )
  ).pipe(startWith(true), shareReplay(1));
  path$ = this.store.pipe(select(selectPath));

  customerRestResource$ = this.store.pipe(
    select(getLoggedInCustomer),
    map(customer => AppFacade.getCustomerRestResource(!customer || customer.isBusinessCustomer)),
    take(1)
  );

  static getCustomerRestResource(isBusinessCustomer: boolean): 'customers' | 'privatecustomers' {
    return !isBusinessCustomer ? 'privatecustomers' : 'customers';
  }

  setBusinessError(error: string) {
    this.store.dispatch(businessError({ error }));
  }

  /**
   * extracts a specific server setting from the store.
   *
   * @param path the path to the server setting, starting from the serverConfig/_config store.
   */
  serverSetting$<T>(path: string) {
    return this.store.pipe(select(getServerConfigParameter<T>(path)));
  }

  // not-dead-code
  /**
   * extracts a specific extra server setting from the store (intended for custom ConfigurationJSON)
   *
   * @param path the path to the server setting, starting from the serverConfig/extra store
   */
  extraSetting$<T>(path: string) {
    return this.store.pipe(select(getExtraConfigParameter<T>(path)));
  }

  /**
   * returns the currency symbol for the currency parameter in the current locale.
   * If no parameter is given, the the default currency is taken instead of it.
   *
   * @param currency The currency
   */
  currencySymbol$(currency?: string) {
    return this.currentLocale$.pipe(
      whenTruthy(),
      withLatestFrom(this.currentCurrency$),
      map(([locale, defaultCurrency]) => getCurrencySymbol(currency || defaultCurrency, 'narrow', locale))
    );
  }

  countries$() {
    this.store.dispatch(loadCountries());
    return this.store.pipe(select(getAllCountries));
  }

  regions$(countryCode: string) {
    this.store.dispatch(loadRegions({ countryCode }));
    return this.store.pipe(select(getRegionsByCountryCode(countryCode)));
  }

  /* DIAS CUSTOMIZATION STARTS */

  animationWrapperClasses$ = combineLatest([
    this.store.pipe(
      select(isStickyHeader),
      map(isSticky => (isSticky ? 'slide-in' : 'slide-out'))
    ),
    this.store.pipe(
      select(getDeviceType),
      map(deviceType => deviceType)
    ),
  ]).pipe(map(classes => classes.filter(c => (!!c && classes[1] === 'desktop' ? classes[0] : ''))));

  getDesktopHeaderHeight$ = combineLatest([
    this.store.pipe(select(getDeviceType)),
    this.store.pipe(select(getHeaderHeight)),
  ]).pipe(
    map(([deviceType, headerHeight]) => {
      if (deviceType === 'desktop') {
        return headerHeight;
      } else {
        return 0;
      }
    })
  );

  customAppWrapperClasses$ = combineLatest([
    this.store.pipe(
      select(getWrapperClass),
      sample(this.router.events.pipe(filter(event => event instanceof NavigationEnd)))
    ),
    this.store.pipe(
      select(getIsTransparentHeader),
      map(isTransparent => (isTransparent ? 'transparent-header' : ''))
    ),
  ]).pipe(map(classes => classes.filter(c => !!c)));

  customHeaderWrapperClasses$ = combineLatest([
    this.store.pipe(select(isStickyHeader)),
    this.store.pipe(select(getDeviceType)),
  ]).pipe(
    map(([isSticky, deviceType]) => {
      if (deviceType === 'desktop' && !isSticky) {
        return ['custom-relative-header'];
      }
      if (isSticky || deviceType !== 'desktop') {
        return ['custom-sticky-header'];
      }

      return ['custom-relative-header'];
    })
  );

  setSeachActive(activeStatus: boolean) {
    this.store.dispatch(setSearchBoxActive({ searchBoxActive: activeStatus }));
  }

  getSeachActive() {
    return this.store.pipe(select(getSearchBoxActive));
  }

  openQuickView(productSku: string) {
    this.store.dispatch(loadProduct({ sku: productSku }));
    const modalRef = this.modalService.open(LazyQuickViewComponent, {
      centered: true,
      windowClass: 'custom-modal-class',
    });
    modalRef.componentInstance.productSku = productSku;
  }
  loadLazyComponent(componentType: 'footer' | 'header' | 'page'): Observable<Type<unknown>> {
    return this.featureToggleService.enabled$('dotCmsUve').pipe(
      filter(enabled => !!enabled),
      switchMap(() => {
        switch (componentType) {
          case 'footer':
            return from(
              import('src/app/extensions/dot-cms-uve/shared/dot-cms-uve-footer/dot-cms-uve-footer.component').then(
                m => m.DotCmsUveFooterComponent
              )
            );
          case 'header':
            return from(
              import('src/app/extensions/dot-cms-uve/shared/dot-cms-uve-header/dot-cms-uve-header.component').then(
                m => m.DotCmsUveHeaderComponent
              )
            );
          case 'page':
            return from(
              import('src/app/extensions/dot-cms-uve/shared/dot-cms-uve-page/dot-cms-uve-page.component').then(
                m => m.DotCmsUvePageComponent
              )
            );
        }
      }),
      catchError(error => {
        console.error(`Failed to load component ${componentType}:`, error);
        return of(null);
      })
    );
  }

  /* DIAS CUSTOMIZATION ENDS */
}
